import '../vendor/aui.css'
import parse, { DOMNode, Element, domToReact } from 'html-react-parser'
import { Alert, Chip, Typography } from '@mui/material'
import { ConfluencePage } from '../types'
import ImageWithPreview from './ImageWithPreview'

interface ConfluencePageArticleProps {
  page: ConfluencePage
  format?: 'view' | 'export_view'
}

export default function ConfluencePageArticle({
  page,
  format = 'view'
}: ConfluencePageArticleProps): React.ReactElement | null {
  /**
   * Transform the node to an alert or chip.
   */
  function transform(node: DOMNode): React.ReactElement | undefined | null {
    if (node.type === 'tag') {
      const element = nodeToElement(node)

      if (shouldTransformToImage(element)) {
        const apiUrl = import.meta.env.PROD
          ? import.meta.env?.VITE_APP_SPINE_API_URL_PRODUCTION
          : import.meta.env?.VITE_APP_SPINE_API_URL_DEVELOPMENT

        const rawSrc = element.attribs?.src

        // Takes the last segment from URI that contains the attachment's title (filename).
        const title = rawSrc?.match(/\/([^/?#]+)(?:[?#].*)?$/)?.[1]

        if (title === undefined) {
          return null
        }

        const attribs = { ...element.attribs }
        delete attribs['data-image-src']
        delete attribs['data-srcset']
        delete attribs['data-media-srcset']

        return (
          <ImageWithPreview
            {...attribs}
            src={`${apiUrl}/v2/confluence/pages/${page.id}/attachments/${title}/download`}
          />
        )
      } else if (shouldTransformToAlert(element)) {
        // Parse alert severity from node.
        const severity = parseAlertSeverity(element)

        // Transform children to inner HTML.
        const innerHtml = domToReact(element.children, {
          replace: transform
        })

        return (
          <Alert
            severity={severity}
            sx={{
              mb: 6,
              ol: {
                paddingInlineStart: '2rem'
              }
            }}
          >
            {innerHtml}
          </Alert>
        )
      } else if (shouldTransformToChip(element)) {
        // Parse chip color from node.
        const color = parseChipColor(element)

        // Parse label from node.
        const label = domToReact(element.children, {
          replace: transform
        })

        return <Chip color={color} label={label} />
      } else {
        return transformElement(element)
      }
    }

    return undefined
  }

  function transformElement(element: Element): React.ReactElement | undefined {
    switch (element.name) {
      case 'h1':
        return (
          <Typography variant="h1">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      case 'h2':
        return (
          <Typography variant="h2">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      case 'h3':
        return (
          <Typography variant="h3">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      case 'h4':
        return (
          <Typography variant="h4">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      case 'h5':
        return (
          <Typography variant="h5">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      case 'h6':
        return (
          <Typography variant="h6">
            {domToReact(element.children, { replace: transform })}
          </Typography>
        )
      default:
        return undefined
    }
  }

  /**
   * Checks if the element has the specified class.
   */
  function hasClass(element: Element, className: string): boolean {
    return element?.attribs.class?.split(' ').includes(className) || false
  }

  /**
   * Checks if the node should be transformed to an alert.
   */
  function shouldTransformToAlert(element: Element): boolean {
    return (
      element?.name === 'div' &&
      (hasClass(element, 'panel') || hasClass(element, 'confluence-information-macro'))
    )
  }

  /**
   * Checks if the node should be transformed to a chip.
   */
  function shouldTransformToChip(element: Element): boolean {
    return element?.name === 'span' && hasClass(element, 'status-macro')
  }

  /**
   * Checks if the node should be transformed to an image.
   */
  function shouldTransformToImage(element: Element): boolean {
    return element?.name === 'img' && hasClass(element, 'confluence-embedded-image')
  }

  /**
   * Parse alert severity from element.
   */
  function parseAlertSeverity(element: Element) {
    if (hasClass(element, 'confluence-information-macro-tip')) {
      return 'success'
    } else if (hasClass(element, 'confluence-information-macro-note')) {
      return 'warning'
    } else if (hasClass(element, 'confluence-information-macro-warning')) {
      return 'error'
    } else {
      return 'info'
    }
  }

  /**
   * Parse the chip color from element.
   */
  function parseChipColor(element: Element) {
    if (hasClass(element, 'aui-lozenge-complete')) {
      return 'info'
    } else if (hasClass(element, 'aui-lozenge-success')) {
      return 'success'
    } else if (hasClass(element, 'aui-lozenge-current')) {
      return 'warning'
    } else if (hasClass(element, 'aui-lozenge-error')) {
      return 'error'
    } else {
      return undefined
    }
  }

  /**
   * Transform a DOM node to an element.
   */
  function nodeToElement(node: DOMNode): Element {
    return node as Element
  }

  return (
    <>
      <article>{parse(page.body[format]?.value ?? '', { replace: transform })}</article>
    </>
  )
}
